import React from 'react';
import { NudgeIcon } from './NudgeLogo';

interface Props { children: React.ReactNode; }
interface State { hasError: boolean; error: Error | null; info: string | null; }

/**
 * Garde global : capture les crashs de rendu React (au lieu d'un écran blanc + reload PWA).
 * Affiche un écran propre + le message d'erreur (pour diagnostiquer, ex : crash du volet clients).
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Visible dans la console + persisté (survit au reload) pour diagnostiquer le crash exact.
    console.error('[Nudge ErrorBoundary] Crash de rendu :', error, info?.componentStack);
    this.setState({ info: info?.componentStack || null });
    try {
      localStorage.setItem('nudge_lastCrash', JSON.stringify({
        when: new Date().toISOString(), url: window.location.href,
        message: error?.message, stack: (error?.stack || '').slice(0, 2000),
        componentStack: (info?.componentStack || '').slice(0, 2000),
      }));
    } catch { /* ignore */ }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, info: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = this.state.error?.message || 'Erreur inconnue';
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 bg-neutral-50 dark:bg-neutral-950 text-center">
        <NudgeIcon className="w-14 h-14 opacity-80" />
        <div>
          <h1 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">Oups, un souci d'affichage</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 max-w-md">
            Pas de panique, rien n'est perdu. Recharge la page — si ça revient, envoie-moi le détail ci-dessous.
          </p>
        </div>
        <pre className="max-w-md w-full overflow-auto text-left text-xs bg-neutral-100 dark:bg-neutral-900 text-red-600 dark:text-red-400 rounded-lg p-3 border border-neutral-200 dark:border-neutral-800">
          {msg}
        </pre>
        <div className="flex gap-2">
          <button
            onClick={this.handleReset}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            Réessayer
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700"
          >
            Recharger l'app
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
